// Partial attendance. Prorating by minutes is wrong in two directions.
//
//     u_partial = u_full * f(fraction) * g(position)
//
// f collapses the value of a five-minute drop-in, because arriving after a ten
// minute walk to catch the last four bars is worse than not going. It is a smooth
// threshold rather than a cliff so the solver does not oscillate at the boundary.
//
// g weights the closing third above the opening third, because sets are built to
// peak late. Both are scaled so that full attendance scores exactly 1.0, which is
// what lets the DP compare a partial block against a full one on one scale.

import type { TuningConfig } from './config.ts';
import { MINUTE } from './time.ts';

function logistic(x: number, midpoint: number, softness: number): number {
  return 1 / (1 + Math.exp(-(x - midpoint) / Math.max(1e-6, softness)));
}

/**
 * f(fraction) with both gates applied: the proportional floor and the absolute
 * minutes floor. Scaled so a fully attended set of `durationMs` returns 1.
 */
export function fractionValue(
  attendedMs: number,
  durationMs: number,
  cfg: TuningConfig,
): number {
  if (durationMs <= 0) return 0;
  const frac = Math.max(0, Math.min(1, attendedMs / durationMs));
  const mins = attendedMs / MINUTE;
  const fullMins = durationMs / MINUTE;

  const gateFrac = logistic(frac, cfg.fractionFloor, cfg.fractionSoftness);
  const gateMins = logistic(mins, cfg.minutesFloor, cfg.minutesSoftness);
  const gateFracFull = logistic(1, cfg.fractionFloor, cfg.fractionSoftness);
  const gateMinsFull = logistic(fullMins, cfg.minutesFloor, cfg.minutesSoftness);

  const denom = gateFracFull * gateMinsFull;
  if (denom <= 0) return 0;
  return (frac * gateFrac * gateMins) / denom;
}

/** Integral of the position weight over [a, b], both in 0..1 of the set. */
export function positionIntegral(a: number, b: number, cfg: TuningConfig): number {
  const lo = Math.max(0, Math.min(1, a));
  const hi = Math.max(0, Math.min(1, b));
  if (hi <= lo) return 0;
  const w = cfg.positionWeights;
  const bounds = [0, 1 / 3, 2 / 3, 1];
  const weights = [w.opening, w.middle, w.closing];
  let total = 0;
  for (let i = 0; i < 3; i++) {
    const segLo = Math.max(lo, bounds[i]);
    const segHi = Math.min(hi, bounds[i + 1]);
    if (segHi > segLo) total += (segHi - segLo) * weights[i];
  }
  return total;
}

/** Mean weight over a whole set, which is what makes a full set score 1.0. */
export function meanPositionWeight(cfg: TuningConfig): number {
  const w = cfg.positionWeights;
  return (w.opening + w.middle + w.closing) / 3;
}

/**
 * g(position) for attendance spanning [a, b] as fractions of the set.
 * Returns 1.0 for a full set by construction.
 */
export function positionValue(a: number, b: number, cfg: TuningConfig): number {
  const span = Math.max(0, Math.min(1, b)) - Math.max(0, Math.min(1, a));
  if (span <= 0) return 0;
  return positionIntegral(a, b, cfg) / (span * meanPositionWeight(cfg));
}

/**
 * The whole curve for one attendance span, as a multiplier on u_full.
 * Timestamps are absolute; the set's own bounds convert them to fractions.
 */
export function attendanceMultiplier(
  setStart: number,
  setEnd: number,
  attendStart: number,
  attendEnd: number,
  cfg: TuningConfig,
): number {
  const duration = setEnd - setStart;
  if (duration <= 0) return 0;
  const s = Math.max(setStart, attendStart);
  const e = Math.min(setEnd, attendEnd);
  if (e <= s) return 0;
  const a = (s - setStart) / duration;
  const b = (e - setStart) / duration;
  return fractionValue(e - s, duration, cfg) * positionValue(a, b, cfg);
}

/**
 * The additive form the DP accrues per interval.
 *
 * The DP walks breakpoints and has to add a number for each interval without
 * knowing what the rest of the path will attend, so it uses the exact position
 * integral (which is additive) with a linear fraction term (which is not gated).
 * The gate is applied afterwards by `attendanceMultiplier` when the path is
 * scored for real, and the solver drops any set whose final attendance falls
 * under the floor and re-solves. Keeping the two forms in one file is deliberate:
 * they have to stay consistent, and they are easy to drift apart.
 */
export function intervalAccrual(
  setStart: number,
  setEnd: number,
  from: number,
  to: number,
  cfg: TuningConfig,
): number {
  const duration = setEnd - setStart;
  if (duration <= 0) return 0;
  const s = Math.max(setStart, from);
  const e = Math.min(setEnd, to);
  if (e <= s) return 0;
  const a = (s - setStart) / duration;
  const b = (e - setStart) / duration;
  return positionIntegral(a, b, cfg) / meanPositionWeight(cfg);
}
